import { css, Theme } from '@emotion/react';
import { AccountCircle, CardMembership, EmojiEvents } from '@mui/icons-material';
import React from 'react';
import { generatePath, useParams } from 'react-router-dom';

import { SettingsSidebar } from 'shared/components/navigation';
import { Box } from 'shared/components/ui';
import { ROUTES } from 'shared/constants';
import { useFeatureEnabled } from 'shared/hooks';
import { useUserById } from 'shared/hooks/useUserById';

import { MemberSidebarUserProfile } from './MemberSidebarUserProfile';

const containerCss = (theme: Theme) => css`
  border-right-style: solid;
  border-right-width: 1px;
  border-right-color: ${theme.palette.divider};
`;

export function MemberInformationSidebar() {
  const { id: userId } = useParams<{ id: string }>();
  const { user, loading } = useUserById({ id: userId });

  const { enabled: showRewardingAccount } = useFeatureEnabled('rewardActions');
  const { enabled: showMemberships } = useFeatureEnabled('communityMarketplace');

  const sidebarItems = React.useMemo(() => {
    const items = [
      {
        title: 'Account Information',
        icon: <AccountCircle />,
        route: generatePath(ROUTES.admin.members.memberInformation, { id: userId }),
      },
    ];

    if (showRewardingAccount) {
      items.push({
        title: 'Rewards Account',
        icon: <EmojiEvents />,
        route: generatePath(ROUTES.admin.members.memberRewardAccount, { id: userId }),
      });
    }

    if (showMemberships) {
      items.push({
        title: 'Memberships',
        icon: <CardMembership />,
        route: generatePath(ROUTES.admin.members.memberMemberships, { id: userId }),
      });
    }

    return items;
  }, [userId, showRewardingAccount, showMemberships]);

  return (
    <Box css={containerCss}>
      <MemberSidebarUserProfile user={user} loading={loading} />
      <SettingsSidebar items={sidebarItems} />
    </Box>
  );
}
